import { Venue } from "common/types";
import { formatDateShort, findImageByName } from "common/utils/general";
import OpeningHours from "./TodaysOpeningHours";
import { bucketUrlPrefix } from "common/config";
import { useAuth, useFilters, useI18N } from "common/hooks";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { HeartStraight, MapPin } from "phosphor-react";
import LOGO from "../../assets/images/smalllogo.svg";
import GREY_LOGO from "../../assets/images/greysmalllogo.svg";
import Modal from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { useState } from "react";
import { XIcon } from "lucide-react";

interface VenueCardProps {
  onOpen?: (location: Venue) => void;
  location: Venue;
  savedPlaces: unknown;
  onSaveVenue?: (location: Venue) => void;
  isNearbyBusinessCard?: boolean;
}

function VenueCard({
  onOpen,
  location,
  savedPlaces,
  onSaveVenue,
  isNearbyBusinessCard,
}: VenueCardProps) {
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const i18n = useI18N();
  const { businessTypesFrontend } = useFilters();
  const { user } = useAuth();
  const lastUpdated = formatDateShort(location?.latestReviewDateUpdated || "");
  const { showModal } = useAuthModals();

  const handleOpenAuthModal = () => {
    if (!user) {
      showModal(AuthModalScreen.EnterEmail);
    }
  };

  const businessTypeImage =
    bucketUrlPrefix +
    findImageByName(
      location?.calcVenueType || location?.businessType,
      businessTypesFrontend
    );

  let businessphotoThumb = "";
  let businessphotoThumbClass = "";
  const placeHolderImage = "/images/business_default_image.svg";
  if (location?.photoThumb) {
    businessphotoThumb = bucketUrlPrefix + location?.photoThumb;
    businessphotoThumbClass =
      "w-full h-[215px] cursor-pointer object-cover img-responsive";
  } else {
    businessphotoThumb = businessTypeImage;
    businessphotoThumbClass =
      "w-full max-h-[80px] cursor-pointer object-contain filter brightness-0 dark:brightness-[10]";
  }

  // Function to handle image load failure
  const handleImageLoadError = (event) => {
    event.target.src = placeHolderImage;
    event.target.style = "object-fit:contain";
  };

  const isVenueSaved = (location) => {
    if (savedPlaces) {
      const flattened = (savedPlaces as any)
        ?.map((e) => e.saved_place_details.map((e) => e.business.id))
        .flat();
      const result = flattened.some((id) => id === location?.business.id);
      return result;
    }
    return false;
  };

  return (
    <div
      className="bg-primary-50 dark:bg-[#313A71] rounded-[8px] p-[12px] md:p-[16px]"
      tabIndex={0}
      role="listitem"
      aria-label={`${location.name}`}
    >
      <article className="">
        <figure
          style={{ height: !location?.photoThumb ? 215 : undefined }}
          className="relative rounded-lg overflow-hidden bg-primary-100 dark:bg-[#37354D] flex justify-center items-center"
          role="none"
        >
          <img
            src={businessphotoThumb}
            alt={location.name}
            className={businessphotoThumbClass}
            onError={handleImageLoadError}
            onClick={() => {
              onOpen(location);
            }}
            aria-hidden="true"
          />

          <div
            className="absolute top-3 left-3 opacity-90 px-3 py-1 bg-primary-50 dark:bg-[#313A71] rounded-3xl flex items-center gap-1"
            tabIndex={0}
            aria-label={location.calcVenueType || location.businessType}
          >
            <div>
              <img
                aria-hidden="true"
                src={businessTypeImage}
                alt={location.calcVenueType || location.businessType}
                className="w-4 h-4 object-contain filter brightness-0 dark:brightness-[10] mr-1"
              />
            </div>
            <div className="button-small-medium text-primary-900 dark:text-primary-150">
              {location.calcVenueType || location.businessType}
            </div>
          </div>
          <div
            className="absolute top-2 right-2 bg-[#404049] rounded-full p-1 bg-opacity-30"
            tabIndex={0}
            role="button"
            onClick={() =>
              !user?.name ? handleOpenAuthModal() : onSaveVenue(location)
            }
            aria-label="Save to list"
          >
            <HeartStraight
              size={24}
              weight={isVenueSaved(location) ? "fill" : "regular"}
              color={isVenueSaved(location) ? "red" : "white"}
              aria-hidden="true"
            />
          </div>

          <div
            className="absolute bottom-2 right-2 p-1 bg-opacity-30"
            tabIndex={0}
            role="button"
            aria-label="QR Code"
            onClick={() => setIsQRModalOpen(true)}
          >
            {isNearbyBusinessCard && location.business.qrCode && (
              <div className="mt-2">
                <img
                  src={bucketUrlPrefix + location.business.qrCode}
                  alt="Venue QR Code"
                  className="w-12 h-12 object-contain"
                />
              </div>
            )}
          </div>
        </figure>

        <div className="">
          <h2
            className="mt-2 cursor-pointer"
            onClick={() => {
              onOpen(location);
            }}
          >
            <p
              className="text-primary-800 dark:text-primary-100 desktop-h3-small-semi-bold truncate text-ellipsis flex-1 max-w-[70vw] "
              tabIndex={0}
            >
              {location.name}
            </p>
          </h2>

          <div
            className="paragraph-small-regular flex flex-row items-center justify-stretch cursor-pointer py-1"
            onClick={() => onOpen(location)}
            tabIndex={0}
            aria-label={location.address}
          >
            <div>
              <MapPin
                size={24}
                className="!text-primary-500 dark:!text-primary-300"
                aria-hidden="true"
              />
            </div>
            <p className="flex-1 max-w-[70vw] truncate text-ellipsis text-primary-800 dark:text-primary-100 ml-[4px]">
              {location.address}
            </p>
          </div>

          {location.openingHours && location.openingHours[0]?.mode === 0 && (
            <OpeningHours
              openingHours={location.openingHours}
              containerClassName=""
              className="paragraph-small-regular text-primary-800 dark:text-primary-100"
            />
          )}

          <div className="my-1 ml-[2px] flex items-start flex-col gap-2">
            {lastUpdated ? (
              <div
                className="flex flex-row items-center"
                tabIndex={0}
                aria-label={`${i18n.get("lastUpdated")} ${lastUpdated}`}
              >
                <img
                  src={LOGO}
                  alt=""
                  width={20}
                  height={20}
                  className="mr-[8px]"
                  aria-hidden="true"
                />
                <p className="paragraph-small-semibold">
                  {i18n.get("lastUpdated")}:
                </p>
                <p className="paragraph-small-regular ml-1">{lastUpdated}</p>
              </div>
            ) : (
              <p
                className=" paragraph-small-regular flex flex-row items-center"
                tabIndex={0}
              >
                <img
                  src={GREY_LOGO}
                  alt=""
                  width={20}
                  height={20}
                  className="mr-[8px]"
                  aria-hidden="true"
                />
                {i18n.get("noAccessibilityInformation")}
              </p>
            )}
          </div>
        </div>
      </article>

      {isQRModalOpen && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={isQRModalOpen}
          onClose={() => setIsQRModalOpen(false)}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none w-[45vh] md:w-[70vh]",
            closeButton: "hidden",
          }}
        >
          <div role="dialog" aria-label="QrCode" tabIndex={0}>
            <section className="flex flex-col bg-white dark:bg-primary-900 rounded-xl overflow-y-auto">
              <header className="py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 sticky top-0">
                <div className="desktop-h1-xxsmall-bold text-center text-primary-800 dark:text-primary-150">
                  {location.name}
                </div>
              </header>

              <div
                aria-label="Modal Content"
                role="none"
                className="flex-grow overflow-y-auto py-3 px-3"
              >
               
                <div className="w-full flex justify-center">
                  {location.business.qrCode && (
                    <img
                      src={bucketUrlPrefix + location.business.qrCode}
                      alt="Venue QR Code"
                      className="w-82 h-82 object-contain"
                    />
                  )}
                </div>

                {/* <p className="mt-2 text-center paragraph-medium-semi-bold text-primary-800 dark:text-primary-150">
                  Scan the QR code to open in mobile app.
                </p> */}
              </div>

              <button
                onClick={() => setIsQRModalOpen(false)}
                className="absolute top-4 left-4"
                aria-label="Close modal"
              >
                <XIcon />
              </button>
            </section>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default VenueCard;
